/**
 * Personal Tab — user's own challenges: active, completed, created.
 * Shows in "Shaxsiy" main tab.
 */

import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Shield } from 'lucide-react'
import { ChallengeCard } from './challenge-card'
import { Skeleton } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'
import type { useLeaveChallenge } from '@turnifly/shared/hooks'
import type { UserChallengeItem } from '@turnifly/shared/api'

interface PersonalTabProps {
  challenges: { active: UserChallengeItem[]; completed: UserChallengeItem[]; created: UserChallengeItem[] }
  isLoading: boolean
  userId: string
  activeOpen: boolean
  completedOpen: boolean
  createdOpen: boolean
  setActiveOpen: (v: boolean) => void
  setCompletedOpen: (v: boolean) => void
  setCreatedOpen: (v: boolean) => void
  leaveMutation: ReturnType<typeof useLeaveChallenge>
}

export function PersonalTab({
  challenges, isLoading: loading, userId,
  activeOpen, completedOpen, createdOpen,
  setActiveOpen, setCompletedOpen, setCreatedOpen,
  leaveMutation,
}: PersonalTabProps): React.ReactElement {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="space-y-2.5">
        <Skeleton className="h-[100px] w-full rounded-2xl" />
        <Skeleton className="h-[100px] w-full rounded-2xl" />
      </div>
    )
  }

  const isEmpty = challenges.active.length === 0
    && challenges.completed.length === 0
    && challenges.created.length === 0

  if (isEmpty) {
    return (
      <EmptyState
        icon={<Shield size={32} className="text-text-hint" />}
        title={t('challenges.noChallenges')}
        description={t('challenges.noChallengesDesc')}
      />
    )
  }

  return (
    <div className="space-y-3">
      {challenges.active.length > 0 && (
        <AccordionGroup title={t('challenges.active')} count={challenges.active.length} isOpen={activeOpen} onToggle={() => setActiveOpen(!activeOpen)}>
          <div className="space-y-2">
            {challenges.active.map((c) => (
              <ChallengeCard
                key={c.id}
                challengeId={c.challenge.id}
                name={c.challenge.name}
                coverEmoji={c.challenge.cover_emoji}
                coverGradient={c.challenge.cover_gradient}
                coverImageUrl={c.challenge.cover_image_url}
                type={c.challenge.type}
                status={c.challenge.status}
                startDate={c.challenge.start_date}
                endDate={c.challenge.end_date}
                durationDays={c.challenge.duration_days}
                membersCount={c.challenge.members_count}
                attemptNumber={c.attempt_number}
                completionRate={c.completion_rate}
                memberStatus={c.status}
                shortCode={c.challenge.short_code}
                isCreator={c.challenge.creator_id === userId}
                onLeave={(cid) => leaveMutation.mutate({ challengeId: cid, userId })}
              />
            ))}
          </div>
        </AccordionGroup>
      )}
      {challenges.completed.length > 0 && (
        <AccordionGroup title={t('challenges.completed')} count={challenges.completed.length} isOpen={completedOpen} onToggle={() => setCompletedOpen(!completedOpen)}>
          <div className="space-y-2">
            {challenges.completed.map((c) => (
              <ChallengeCard
                key={c.id}
                challengeId={c.challenge.id}
                name={c.challenge.name}
                coverEmoji={c.challenge.cover_emoji}
                coverGradient={c.challenge.cover_gradient}
                coverImageUrl={c.challenge.cover_image_url}
                type={c.challenge.type}
                status={c.challenge.status}
                durationDays={c.challenge.duration_days}
                membersCount={c.challenge.members_count}
                attemptNumber={c.attempt_number}
                completionRate={c.completion_rate}
                memberStatus={c.status}
                shortCode={c.challenge.short_code}
                isCreator={c.challenge.creator_id === userId}
              />
            ))}
          </div>
        </AccordionGroup>
      )}
      {challenges.created.length > 0 && (
        <AccordionGroup title={t('challenges.created')} count={challenges.created.length} isOpen={createdOpen} onToggle={() => setCreatedOpen(!createdOpen)}>
          <div className="space-y-2">
            {challenges.created.map((c) => (
              <ChallengeCard
                key={c.id}
                challengeId={c.challenge.id}
                name={c.challenge.name}
                coverEmoji={c.challenge.cover_emoji}
                coverGradient={c.challenge.cover_gradient}
                coverImageUrl={c.challenge.cover_image_url}
                type={c.challenge.type}
                status={c.challenge.status}
                durationDays={c.challenge.duration_days}
                membersCount={c.challenge.members_count}
                shortCode={c.challenge.short_code}
                isCreator
              />
            ))}
          </div>
        </AccordionGroup>
      )}
    </div>
  )
}

// ── Accordion Group ───────────────────────────────────────────

function AccordionGroup({ title, count, isOpen, onToggle, children }: {
  title: string; count: number; isOpen: boolean; onToggle: () => void; children: React.ReactNode
}): React.ReactElement {
  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 tap-highlight-transparent">
        <span className="text-[14px] font-semibold text-text">
          {title} <span className="text-text-hint/50 font-normal">({count})</span>
        </span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={16} className="text-text-hint" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
